import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import useAxiosInstance from '../../../services/useAxiosInstance';
import Loading from '../../../component/Loading/Loading';
import AlertMsg from '../../../component/AlertMsg/AlertMsg';

const BankDetails = () => {
  const axiosInstance = useAxiosInstance();
  const communityId = useSelector((state) => state.auth.communityId);
  const userId = useSelector((state) => state.auth.userId);
  const [isEditing, setIsEditing] = useState(false);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [originalBankDetails, setOriginalBankDetails] = useState(null);
  const [bankDetails, setBankDetails] = useState({
    accountHolderName: "",
    accountNumber: "",
    confirmAccountNumber: "",
    bankId: 0,
    bankName: "",
    communityId: communityId,
    createdById: userId,
    createdDate: "",
    isVerified: true,
    modifiedById: userId,
    modifiedDate: new Date().toISOString(),
    routingNumber: ""
  });

  const [alert, setAlert] = useState({ visible: false, message: '', type: '' });

  useEffect(() => {
    fetchBankDetails();
  }, [communityId]);

  const fetchBankDetails = async () => {
    setLoading(true);
    try {
      const response = await axiosInstance.get(`/api/public/bankdetail/bankDetails/${communityId}`);
      setOriginalBankDetails(response.data); // Store original data
      setBankDetails((prevDetails) => ({
        ...prevDetails,
        ...response.data,
        confirmAccountNumber: response.data.accountNumber
      }));

    } catch (error) {
      console.error('Error fetching bank details:', error);
      setAlert({ visible: true, message: 'Error fetching bank details: Please try again.', type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = () => {
    setIsEditing(true);
  };

  const handleCancel = () => {
    setIsEditing(false);
    setErrors({});
    if (originalBankDetails) {
      setBankDetails({
        ...originalBankDetails,
        confirmAccountNumber: originalBankDetails.accountNumber
      });
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setBankDetails((prevDetails) => ({
      ...prevDetails,
      [name]: value
    }));
    setErrors((prevErrors) => ({
      ...prevErrors,
      [name]: ''
    }));
  };

  const validate = () => {
    let tempErrors = {};
    if (!bankDetails.accountHolderName) tempErrors.accountHolderName = "Account Holder Name is required.";
    if (!bankDetails.bankName) tempErrors.bankName = "Bank Name is required.";

    const accountNumber = bankDetails.accountNumber.trim();
    const confirmAccountNumber = bankDetails.confirmAccountNumber.trim();

    if (!accountNumber) {
      tempErrors.accountNumber = 'Account Number is required.';
    } else if (!/^\d{13}$/.test(accountNumber)) {
      tempErrors.accountNumber = 'Account Number must be a 13-digit number.';
    }

    if (!confirmAccountNumber) {
      tempErrors.confirmAccountNumber = 'Confirm Account Number is required.';
    } else if (!/^\d{13}$/.test(confirmAccountNumber)) {
      tempErrors.confirmAccountNumber = 'Confirm Account Number must be a 13-digit number.';
    } else if (confirmAccountNumber !== accountNumber) {
      tempErrors.confirmAccountNumber = 'Confirm Account Number must match Account Number.';
    }

    if (!bankDetails.routingNumber) tempErrors.routingNumber = "Routing Number is required.";

    setErrors(tempErrors);
    return Object.keys(tempErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    setLoading(true);

    try {
      const response = await axiosInstance.put(`/api/public/bankdetail/update/${bankDetails.bankId}`,
        bankDetails
      );

      setAlert({ visible: true, message: 'Bank details updated successfully!', type: 'success' });
      setIsEditing(false);
    } catch (error) {
      console.error('Error updating bank details:', error);
      setAlert({ visible: true, message: 'Error updating bank details. Please try again.', type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bg-gray-50 min-h-screen ">
      {loading && <Loading />}
      {!loading && (
        <div className="max-w-7xl mx-auto">
          {alert.visible && (
            <AlertMsg
              message={alert.message}
              type={alert.type}
              onClose={() => setAlert({ ...alert, visible: false })}
            />
          )}

          <div className="flex justify-between items-center mb-6">
            <h1 className="text-2xl font-bold text-gray-800">Bank Details</h1>
            <div className="flex items-center">
              {!isEditing && (
                <button
                  className="bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded-md"
                  onClick={handleEdit}
                >
                  Edit
                </button>
              )}
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-sm overflow-hidden mb-6">
            <form className="p-4" onSubmit={handleSubmit}>
              <div className="flex flex-wrap gap-6 mb-6">
                <div className="w-full md:w-[calc(50%-1.5rem)]">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Account Holder Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    name="accountHolderName"
                    value={bankDetails.accountHolderName}
                    onChange={handleChange}
                    readOnly={!isEditing}
                    className={`w-full rounded-md border ${errors.accountHolderName ? "border-red-500" : "border-gray-300"} px-4 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 ${!isEditing ? "bg-gray-50" : ""}`}
                  />
                  {errors.accountHolderName && <p className="text-red-500 text-xs mt-1">{errors.accountHolderName}</p>}
                </div>

                <div className="w-full md:w-[calc(50%-1.5rem)]">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Bank Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    name="bankName"
                    value={bankDetails.bankName}
                    onChange={handleChange}
                    readOnly={!isEditing}
                    className={`w-full rounded-md border ${errors.bankName ? "border-red-500" : "border-gray-300"} px-4 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 ${!isEditing ? "bg-gray-50" : ""}`}
                  />
                  {errors.bankName && <p className="text-red-500 text-xs mt-1">{errors.bankName}</p>}
                </div>

                <div className="w-full md:w-[calc(50%-1.5rem)]">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Account Number <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    name="accountNumber"
                    value={bankDetails.accountNumber}
                    onChange={handleChange}
                    readOnly={!isEditing}
                    onKeyPress={(e) => {
                      if (!/[0-9]/.test(e.key)) {
                        e.preventDefault();
                      }
                    }}
                    maxLength="13"
                    className={`w-full rounded-md border ${errors.accountNumber ? "border-red-500" : "border-gray-300"} px-4 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 ${!isEditing ? "bg-gray-50" : ""}`}
                  />
                  {errors.accountNumber && <p className="text-red-500 text-xs mt-1">{errors.accountNumber}</p>}
                </div>

                <div className="w-full md:w-[calc(50%-1.5rem)]">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Re-confirm Account Number <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="password"
                    name="confirmAccountNumber"
                    value={bankDetails.confirmAccountNumber}
                    onChange={handleChange}
                    readOnly={!isEditing}
                    onKeyPress={(e) => {
                      if (!/[0-9]/.test(e.key)) {
                        e.preventDefault();
                      }
                    }}
                    maxLength="13"
                    className={`w-full rounded-md border ${errors.confirmAccountNumber ? "border-red-500" : "border-gray-300"} px-4 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 ${!isEditing ? "bg-gray-50" : ""}`}
                  />
                  {errors.confirmAccountNumber && <p className="text-red-500 text-xs mt-1">{errors.confirmAccountNumber}</p>}
                </div>

                <div className="w-full md:w-[calc(50%-1.5rem)]">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Routing Number <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    name="routingNumber"
                    value={bankDetails.routingNumber}
                    onChange={handleChange}
                    readOnly={!isEditing}
                    className={`w-full rounded-md border ${errors.routingNumber ? "border-red-500" : "border-gray-300"} px-4 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 ${!isEditing ? "bg-gray-50" : ""}`}
                  />
                  {errors.routingNumber && <p className="text-red-500 text-xs mt-1">{errors.routingNumber}</p>}
                </div>
              </div>

              {isEditing && (
                <div className="flex justify-between items-center">
                  <div className="text-sm text-red-500">* indicates required field</div>
                  <div className="flex gap-2">
                    <button
                      type="button"
                      className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
                      onClick={handleCancel}
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      className="px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600"
                    >
                      Save
                    </button>
                  </div>
                </div>
              )}
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default BankDetails;